import React from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { getLang } from 'shared/store/selectors/lang.selector';
import { ScreenTitle } from 'src/components/design-system';
import { useDateFormatter } from 'shared/hooks/dates.hooks';
import changelog from 'src/constants/changelog.json';
import styles from './changelog.module.scss';

// Release dates are date-only strings; build them at *local* midnight so the
// formatter renders the intended calendar day in every timezone.
const toLocalDate = value => {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day);
};

const groupByYear = releases =>
  releases.reduce((groups, release) => {
    const year = release.date.slice(0, 4);
    const last = groups[groups.length - 1];

    if (last?.year === year) last.releases.push(release);
    else groups.push({ year, releases: [release] });

    return groups;
  }, []);

// Static import — resolved once, so grouping never needs to re-run per render.
const RELEASES = changelog.releases ?? [];
const RELEASE_GROUPS = groupByYear(RELEASES);

const Release = React.memo(function Release({ release, lang, formatDate }) {
  const { version, date, changes = [], maintenance } = release;

  return (
    <article className={styles.release}>
      <div className={styles.stamp}>
        <span className={styles.version}>{version}</span>
        <time className={styles.date} dateTime={date}>
          {formatDate(date)}
        </time>
      </div>
      <ul className={styles.changes}>
        {changes.map(change => (
          <li key={change}>{change}</li>
        ))}
        {maintenance && <li className={styles.maintenance}>{lang.MAINTENANCE}</li>}
      </ul>
    </article>
  );
});

export const ChangelogPage = React.memo(function ChangelogPage() {
  const lang = useSelector(getLang('CHANGELOG'));
  const { formatLongDate } = useDateFormatter();

  const formatDate = value => formatLongDate(toLocalDate(value));

  return (
    <div className={classNames('new-design-system', styles.root)}>
      <ScreenTitle title={lang.TITLE} subtitle={lang.SUBTITLE} />

      {RELEASE_GROUPS.length === 0 && <p className={styles.empty}>{lang.EMPTY}</p>}

      {RELEASE_GROUPS.map(({ year, releases: yearReleases }) => (
        <section key={year} className={styles.yearSection}>
          <h2 className={styles.year}>{year}</h2>
          {yearReleases.map(release => (
            <Release key={release.version} release={release} lang={lang} formatDate={formatDate} />
          ))}
        </section>
      ))}
    </div>
  );
});
